/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import clsx from 'clsx';

// CONSTANTS
import { ROUTE_WP_PAGE, ROUTE_PARTNER_WP_PAGE } from '@/constants/routes';

// UTILITY
import '@/utility/jquery';
import {
  camelToSnakeCase,
  convertHTMLEntity,
  getSearchParams,
  removeHTMLEntity,
} from '@/utility/common';
import { getLink } from '@/utility/routes';
import { externalLink, analyticsPageLoaded } from '@/utility/analytics';
import {
  siteUrl,
  wpComposerPublicPath,
  wpComposerAssetsPath,
  runComposer,
  removeCss,
  manageClickListeners,
  mainComposerScripts,
  WPData,
  connectScripts,
  removeScripts,
} from '@/utility/wp';
import { getSubscriptionLink } from '@/utility/subscription';

// STORE
import { AuthActions, WPBlogActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import ReactHtml from '@/components/ReactHtml';
import Loader from '@/components/Loader';
import Link from '@/components/Link';

// STYLES
import { Section } from '@/styles/wp';
import { WPContent } from './styles';

const wpData = new WPData();

class WPPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mainCssLoaded: false,
    };
    this.params = getSearchParams(window.location.search);
  }

  componentDidMount() {
    this.getPage();

    window.prerendercloudReady = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { mainCssLoaded } = this.state;
    const {
      data,
      router: {
        params: { slug },
      },
      loading,
      isAuth,
    } = this.props;
    const dataLength = Object.keys(data).length;

    if (prevState.mainCssLoaded !== mainCssLoaded && mainCssLoaded) {
      const externalLinks = document.querySelectorAll('a.wp-external-link');
      const authButtons = document.querySelectorAll('.auth-dialog button');

      manageClickListeners(externalLinks, 'add', this.handleExternalWPLink);
      manageClickListeners(authButtons, 'add', this.handleOpenAuthDialog);
    }

    if (prevProps.data !== data && dataLength) {
      if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
        connectScripts(data.x_metadata.vcvSourceAssetsFiles.jsBundles, wpData);
      }

      if (data?.x_metadata?.vcvSourceCssFileUrl) {
        this.connectCss(wpComposerAssetsPath + data.x_metadata.vcvSourceCssFileUrl);
      } else {
        this.setState({ mainCssLoaded: true });
      }
    }

    if (prevProps.loading && prevProps.loading !== loading && !dataLength) {
      this.setState({ mainCssLoaded: true });
    }

    if (prevProps.router.params.slug !== slug || prevProps.isAuth !== isAuth) {
      this.removeWPFromDOM();
      this.setState({ mainCssLoaded: false }, this.getPage);
    }
  }

  componentWillUnmount() {
    this.removeWPFromDOM();
  }

  getPage = () => {
    const {
      router: {
        params: { slug },
      },
      getWPPage,
    } = this.props;

    getWPPage(slug);

    connectScripts(mainComposerScripts, wpData);
  };

  handleExternalWPLink = e => {
    let dataset = e.currentTarget?.dataset || {};
    dataset = JSON.stringify(dataset);
    dataset = JSON.parse(dataset);

    Object.keys(dataset)
      .map(key => {
        const newKey = camelToSnakeCase(key);
        const value = dataset[key];
        delete dataset[key];
        dataset[newKey] = value;

        return newKey;
      })
      .forEach(key => {
        if (dataset[key] === 'true' || dataset[key] === 'false') {
          dataset[key] = JSON.parse(dataset[key]);
        }
      });

    const property = {
      location: 'WP Page',
      pageTitle: document.title,
      href: e.currentTarget?.href || '',
      ...dataset,
    };

    externalLink(property);
  };

  removeWPFromDOM = () => {
    const { data } = this.props;
    let scripts = mainComposerScripts;

    const externalLinks = document.querySelectorAll('a.wp-external-link');
    const authButtons = document.querySelectorAll('.auth-dialog button');

    manageClickListeners(externalLinks, 'remove', this.handleExternalWPLink);
    manageClickListeners(authButtons, 'remove', this.handleOpenAuthDialog);

    if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
      scripts = [...scripts, ...data.x_metadata.vcvSourceAssetsFiles.jsBundles];
    }

    removeScripts(scripts, wpData);

    if (data?.x_metadata?.vcvSourceCssFileUrl) {
      removeCss(data.x_metadata.vcvSourceCssFileUrl);
    }
  };

  connectCss = path => {
    const head = document.getElementsByTagName('head')[0];
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = path;

    head.appendChild(link);

    link.onload = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
    link.onerror = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
  };

  getCss = cssBundles => {
    return cssBundles.map((cssSrc, index) => {
      let curLink = cssSrc;
      const stringArray = curLink.split('/');
      const hasHTTP = stringArray[0].includes('http');

      if (!hasHTTP) {
        if (curLink.includes('sharedLibraries')) {
          curLink = `${wpComposerAssetsPath}/${curLink}`;
        } else {
          curLink = `${wpComposerPublicPath}/sources/${curLink}`;
        }
      }

      return <link rel="stylesheet" href={curLink} media="all" key={index} />;
    });
  };

  getMeta = data => {
    const metaTags = data.reduce((acc, meta, index) => {
      if (meta.name !== 'robots' && meta.property !== 'og:url') {
        acc.push(<meta key={index} {...meta} />);
      }

      return acc;
    }, []);

    return metaTags.length ? metaTags : null;
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  handleOpenAuthDialog = event => {
    const { manageAuthDialogState, partner } = this.props;
    const parentEl = event.target?.closest('.auth-dialog');
    const hasPartner = !!partner.slug;
    const partnerSettings = partner.settings || {};
    let useAuthorizing = true;
    let screen = 'registration';

    if (hasPartner) {
      useAuthorizing = !!partnerSettings.use_authorizing;
    }

    if (parentEl) {
      if (parentEl.classList.contains('login')) screen = 'login';
    }

    if (useAuthorizing) manageAuthDialogState({ screen });
  };

  getLinkComponent = props => {
    const app_slug = this.params.get('app_slug');
    const plan_id = this.params.get('plan_id');

    if (
      props['data-service-name'] === 'Sling' &&
      props['data-location'] === 'sling-hulu-terms' &&
      app_slug &&
      plan_id
    ) {
      const { href, ...restProps } = props;

      const link = getSubscriptionLink(
        app_slug,
        plan_id,
        this.params.get('addons'),
        this.params.get('zipcode'),
      );

      return <Link {...restProps} to={link} />;
    }

    // eslint-disable-next-line
    return <a {...props} />;
  };

  render() {
    const { mainCssLoaded } = this.state;
    const {
      router: {
        params: { slug },
      },
      isAuth,
      data,
      loading,
      isPartnerDomain,
      partnerSlug,
      partner,
      partnerLoading,
    } = this.props;
    const isPartner = isPartnerDomain || !!partnerSlug;
    const hasData = !!Object.keys(data).length;

    const seoData = {
      canonicalUrl: `${siteUrl}${getLink(ROUTE_WP_PAGE, { slug })}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_WP_PAGE, { partner: partner.slug, slug })}`
        : `${siteUrl}${getLink(ROUTE_WP_PAGE, { slug })}`,
    };

    return (
      <Layout>
        {!loading && hasData && !partnerLoading && mainCssLoaded && (
          <Helmet onChangeClientState={this.handleChangeHelmetData}>
            {data?.yoast_title && <title>{convertHTMLEntity(data.yoast_title)}</title>}
            <link
              rel="stylesheet"
              href={`${wpComposerPublicPath}/dist/front.bundle.css`}
              media="all"
            />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            {data?.x_metadata?.vcvSourceAssetsFiles?.cssBundles &&
              this.getCss(data.x_metadata.vcvSourceAssetsFiles.cssBundles)}
            {data?.x_featured_media_medium && (
              <meta name="twitter:image" content={data.x_featured_media_medium} />
            )}
            <meta name="twitter:title" content={convertHTMLEntity(data.yoast_title)} />
            {data?.excerpt?.rendered && (
              <meta
                name="twitter:description"
                content={`MyBundle - ${removeHTMLEntity(data.excerpt.rendered)}`}
              />
            )}
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
            {data?.yoast_meta && this.getMeta(data.yoast_meta)}
          </Helmet>
        )}
        <Section className="page-content">
          <WPContent
            className={clsx(
              data.slug,
              'post-page',
              isAuth && 'auth',
              isPartner ? 'partner' : 'regular-mb',
            )}
          >
            {hasData && data?.content?.rendered && mainCssLoaded && (
              <ReactHtml
                className="markdown"
                components={{
                  a: this.getLinkComponent,
                }}
              >
                {data.content.rendered.trim()}
              </ReactHtml>
            )}
          </WPContent>
        </Section>
        <Loader fullFilled active={loading || !mainCssLoaded} />
      </Layout>
    );
  }
}

WPPage.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  isAuth: PropTypes.bool.isRequired,
  getWPPage: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  partner: PropTypes.object,
  partnerLoading: PropTypes.bool.isRequired,
  manageAuthDialogState: PropTypes.func.isRequired,
  isPartnerDomain: PropTypes.bool.isRequired,
  partnerSlug: PropTypes.string.isRequired,
};

const mapStateToProps = ({ app, wpBlog, partner, auth }) => ({
  isAuth: auth.isAuth,
  data: wpBlog.pageData,
  loading: wpBlog.loadings.main,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  isPartnerDomain: partner.isPartnerDomain,
  partnerSlug: app.partner,
});

const mapDispatchToProps = dispatch => {
  return {
    getWPPage: slug => dispatch(WPBlogActions.getWPPage.action({ slug })),
    manageAuthDialogState: props =>
      dispatch(AuthActions.manageAuthDialogState.action({ state: true, props })),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
)(WPPage);
